import React from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { 
  Truck, 
  Shield, 
  DollarSign, 
  MapPin,
  ArrowRight,
  CheckCircle,
  Clock,
  FileText
} from 'lucide-react';
import { useSectionContent } from '../hooks/useSiteContent';

// Default content values (BlueLock Freights brand; CMS overrides at runtime)
const HOME_DEFAULTS = {
  home_hero_title: "BlueLock Freights",
  home_hero_subtitle: "Carrier-Managed Trucking Operations",
  home_hero_tagline: "OTR and Regional Opportunities for Owner-Operators, Lease-On Drivers, and Company Drivers",
  home_about_title: "About BlueLock Freights",
  home_about_description: "BlueLock Freights operates as a trade/brand name associated with BlueLock Transport LLC, a Michigan-based motor carrier headquartered at 4370 Chicago Drive SW, Grandville, MI 49418. We operate USDOT 4557330 / MC 1810883 with a focus on regulatory compliance, operational consistency, and long-term driver partnerships. Our structure supports Owner-Operators, Lease-On Drivers, and Company Drivers through organized dispatch coordination, transparent settlement practices, and secure internal systems.",
  home_cta_title: "Professional Drivers Welcome",
  home_cta_description: "Applications are reviewed on a rolling basis. Submit your application today."
};

const PublicHomePage = () => {
  const { content } = useSectionContent('home_page', HOME_DEFAULTS);

  return (
    <div className="min-h-screen bg-white" data-testid="public-home-page">
      <PublicNavbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {content.home_hero_title}
            </h1>
            <p className="text-xl md:text-2xl text-blue-200 font-medium mb-4">
              {content.home_hero_subtitle}
            </p>
            <p className="text-lg text-blue-100 mb-10">
              {content.home_hero_tagline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/drivers"
                data-testid="hero-driver-opportunities-btn"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors group"
              >
                Driver Opportunities
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/apply"
                data-testid="hero-apply-btn"
                className="inline-flex items-center justify-center px-8 py-4 bg-blue-700 text-white border-2 border-blue-600 rounded-lg text-lg font-semibold hover:bg-blue-600 transition-colors"
              >
                Apply to Drive
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white rounded-lg text-lg font-semibold hover:bg-white/10 transition-colors"
              >
                Login Portal
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-6">
              <MapPin className="w-4 h-4 mr-2" />
              Grandville, Michigan
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {content.home_about_title}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {content.home_about_description}
            </p>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Operational Focus
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Structured support for professional drivers seeking reliable freight opportunities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-900" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Regulatory Compliance</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Operations aligned with FMCSA, DOT, and insurance requirements.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Truck className="w-6 h-6 text-blue-900" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Dispatch Coordination</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Organized dispatch support focused on trip planning and operational efficiency.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-blue-900" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Weekly Settlements</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Transparent settlement practices with consistent weekly payment schedules.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-blue-900" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure Systems</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Internal portal for document management, compliance tracking, and communication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Driver Programs Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Driver Partnership Options
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Three structured tracks: Owner-Operators, Lease-On Drivers, and Company Drivers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto" data-testid="home-programs-grid">
            <div className="bg-white rounded-2xl p-8 border border-gray-200" data-testid="home-program-owner-operator">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Owner-Operator Program</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Dispatch-Only: 8% of gross revenue or $400/week per truck</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Weekly settlements</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Driver maintains operational independence under own authority</span>
                </li>
              </ul>
              <Link
                to="/drivers"
                className="text-blue-900 font-medium hover:text-blue-700 inline-flex items-center"
              >
                Learn more <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-200" data-testid="home-program-lease-on">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Lease-On Program</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">84% / 16% revenue split on linehaul</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Weekly settlements</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Company-coordinated dispatch and freight sourcing</span>
                </li>
              </ul>
              <Link
                to="/drivers"
                className="text-blue-900 font-medium hover:text-blue-700 inline-flex items-center"
              >
                Learn more <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-200" data-testid="home-program-company-driver">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Company Driver Program</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Operate BlueLock-provided equipment under BlueLock Transport LLC authority</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Structured pay, defined in the company driver agreement</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Maintenance, fuel, and insurance coordinated by BlueLock</span>
                </li>
              </ul>
              <Link
                to="/drivers"
                className="text-blue-900 font-medium hover:text-blue-700 inline-flex items-center"
              >
                Learn more <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {content.home_cta_title}
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            {content.home_cta_description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/drivers"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              View Opportunities
            </Link>
            <Link
              to="/apply"
              data-testid="cta-apply-btn"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-lg text-lg font-semibold hover:bg-white/10 transition-colors"
            >
              Apply Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default PublicHomePage;
